import { useEffect, useState } from "react";
import { api, fmtDateTime, fileUrl } from "@/lib/api";
import { useAuth, hasRole } from "@/lib/auth";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { StatusPill } from "@/pages/Travel";
import ApprovalTimeline from "@/components/ApprovalTimeline";

/**
 * Reusable approval review dialog. Fetches the approval that belongs to a
 * given underlying request (travel or expense) and lets the user approve /
 * reject inline. Super Admin also gets a "Reject Approved" action for
 * reversing an approved or paid claim.
 */
export default function ApprovalActionDialog({ open, onOpenChange, reqType, reqId, onDone }) {
  const { user } = useAuth();
  const isSuperAdmin = hasRole(user, "super_admin");
  const [approval, setApproval] = useState(null);
  const [comment, setComment] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !reqType || !reqId) return;
    setLoading(true); setComment(""); setApproval(null);
    api.get(`/approvals/by-request/${reqType}/${reqId}`)
      .then((r) => setApproval(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load approval"))
      .finally(() => setLoading(false));
  }, [open, reqType, reqId]);

  const act = async (action) => {
    if (!comment.trim()) return toast.error("Comment is required");
    try {
      const r = await api.post(`/approvals/${approval.id}/action`, { action, comment });
      if (r?.data?.auto_paid) toast.success(`Approved & auto-marked Paid`);
      else toast.success(`Marked ${action}d`);
      onOpenChange(false); onDone?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const markPaid = async () => {
    if (!window.confirm("Mark this as paid?")) return;
    try { await api.post(`/approvals/${approval.id}/mark-paid`); toast.success("Marked paid"); onOpenChange(false); onDone?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const rejectApproved = async () => {
    // Super Admin: comment is optional server-side
    if (!window.confirm("Reverse this approved/paid request back to Rejected? The applicant will be notified.")) return;
    try {
      await api.post(`/approvals/${approval.id}/reject-approved`, { comment });
      toast.success("Reversed to rejected");
      onOpenChange(false); onDone?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const renderDetails = (details) => {
    if (!details) return null;
    const shown = Object.entries(details).filter(([, v]) => v !== null && v !== undefined && v !== "");
    return (
      <div className="text-xs bg-slate-50 dark:bg-slate-800/40 border rounded p-3 space-y-1" style={{ borderColor: "hsl(var(--border))" }}>
        {shown.map(([k, v]) => {
          if (k === "lines" && Array.isArray(v)) {
            return (
              <div key={k}>
                <div className="opacity-60 mb-1">Items:</div>
                <ul className="pl-3 space-y-0.5">
                  {v.map((ln, i) => (
                    <li key={i}>
                      <span className="capitalize">{(ln.expense_type || "").replace(/_/g, " ")}{ln.custom_type ? ` (${ln.custom_type})` : ""}</span>
                      : ₹{ln.amount}
                      {ln.from_location && ln.to_location ? ` — ${ln.from_location} → ${ln.to_location}` : ""}
                      {ln.description ? ` — ${ln.description}` : ""}
                    </li>
                  ))}
                </ul>
              </div>
            );
          }
          if (k === "cost_breakdown" && typeof v === "object" && v) {
            return (
              <div key={k}>
                <div className="opacity-60 mb-1">Cost breakdown:</div>
                <ul className="pl-3 space-y-0.5">
                  {Object.entries(v).map(([kk, vv]) => (
                    <li key={kk} className="capitalize"><span>{kk.replace(/_/g, " ")}:</span> ₹{vv}</li>
                  ))}
                </ul>
              </div>
            );
          }
          return (
            <div key={k}>
              <span className="opacity-60 capitalize">{k.replace(/_/g, " ")}:</span>{" "}
              <span className="font-medium">{typeof v === "object" ? JSON.stringify(v) : String(v)}</span>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="capitalize">{reqType} approval</DialogTitle></DialogHeader>
        {loading && <div className="text-sm opacity-70 py-6 text-center">Loading approval…</div>}
        {approval && (
          <div className="space-y-3">
            <div className="text-sm">{approval.summary}</div>
            <div className="flex items-center gap-2 flex-wrap">
              <StatusPill status={approval.status} />
              {approval.auto_pay_on_approval && approval.status !== "paid" && (
                <span className="text-[10px] uppercase tracking-wider font-semibold text-[#fd7e14] bg-orange-50 dark:bg-orange-950/30 border border-orange-200 dark:border-orange-900/40 px-1.5 py-0.5 rounded">
                  Auto-pay on approval
                </span>
              )}
              {approval.is_delayed && (
                <span className="text-[10px] uppercase tracking-wider font-semibold text-red-600 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900/40 px-1.5 py-0.5 rounded">
                  Delayed · {approval.overdue_hours}h overdue
                </span>
              )}
            </div>

            {(approval.request_attachment_ids?.length > 0 || approval.request_attachment_id) && (
              <div className="text-sm">
                <div className="text-xs uppercase tracking-wider opacity-60 mb-1">Attached document{(approval.request_attachment_ids?.length || 1) > 1 ? "s" : ""}</div>
                <div className="flex flex-wrap gap-2">
                  {(approval.request_attachment_ids && approval.request_attachment_ids.length > 0 ? approval.request_attachment_ids : [approval.request_attachment_id]).filter(Boolean).map((fid, i) => (
                    <a key={fid} href={fileUrl(fid)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#fd7e14] underline text-sm">
                      📎 View {(approval.request_attachment_ids?.length || 1) > 1 ? `#${i + 1}` : "document"}
                    </a>
                  ))}
                </div>
              </div>
            )}

            {renderDetails(approval.request_details)}

            <ApprovalTimeline approval={approval} />


            {(approval.can_act || (isSuperAdmin && ["approved", "paid"].includes(approval.status))) && (
              <Textarea
                data-testid="approval-comment-inline"
                placeholder={approval.can_act ? "Comment (required)…" : "Comment (optional for Super Admin)…"}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
              />
            )}

            <DialogFooter className="flex-wrap gap-2">
              {approval.can_act && (
                <>
                  <Button variant="outline" className="text-red-600" onClick={() => act("reject")} data-testid="approval-inline-reject">Reject</Button>
                  <Button onClick={() => act("approve")} className="btn-brand" data-testid="approval-inline-approve">Approve</Button>
                </>
              )}
              {!approval.can_act && approval.status === "approved" && isSuperAdmin && (
                <>
                  <Button variant="outline" className="text-red-600" onClick={rejectApproved} data-testid="approval-inline-reject-approved">Reject (Reverse Approval)</Button>
                  <Button onClick={markPaid} className="btn-brand" data-testid="approval-inline-mark-paid">Mark as Paid</Button>
                </>
              )}
              {approval.status === "paid" && isSuperAdmin && (
                <Button variant="outline" className="text-red-600" onClick={rejectApproved} data-testid="approval-inline-reject-paid">Reject (Reverse Paid)</Button>
              )}
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
